import React from 'react'
import R from 'ramda'
import { Button, Row, Col, Alert, Table, Input } from 'react-bootstrap'
import { ButtonLink } from 'react-router-bootstrap'
import DocumentTitle from 'react-document-title'
import ReactMixin from 'react-mixin'
import ImmutableRenderMixin from 'react-immutable-render-mixin'
import RecordNav from './recordNav'
import PropertyActions from './propertyActions'
import EditableTable from '_/shared/components/editableTable'
import GeocodeMap from '_/shared/components/geocodeMap'
import ConfirmModal from '_/shared/components/confirmModal'
import PhotoGallery from '_/shared/components/photoGallery'
import MarkDuplicateModal from '_/shared/components/markDuplicateModal'
import PersonalMapping from '_/shared/components/personalMapping'
import ImprovementsTable from '_/shared/components/improvementsTable'
import LandUseTable from '_/shared/components/landUseTable'
import createLinkToImmutable from '_/utils/createLinkToImmutable'
import ensureNumber from '_/utils/ensureNumber'
import { Map, List } from 'immutable'

/**
 * Property detail page
 */
@ReactMixin.decorate(ImmutableRenderMixin)
class PropertyDetail extends React.Component {
  static propTypes = {
    account: React.PropTypes.object.isRequired,
    improvements: React.PropTypes.object.isRequired,
    property: React.PropTypes.object.isRequired,
    photos: React.PropTypes.object.isRequired,
    metro: React.PropTypes.object.isRequired
  }

  static contextTypes = {
    store: React.PropTypes.object,
    router: React.PropTypes.func,
    notify: React.PropTypes.func
  }

  static fetchData (store, { params }) {
    return {
      landUses: store.get(`/properties/${params.id}/landUse`),
      improvements: store.get(`/properties/${params.id}/improvements`),
      property: store.get(`/properties/${params.id}`, { expanded: true }),
      photos: store.get(`/properties/${params.id}/photos`),
      metro: store.get('/account/metro')
    }
  }

  constructor (props, context) {
    super(props, context)
    this.setStateFromProps(props, context)
  }
  componentWillReceiveProps (...args) { this.setStateFromProps(...args) }

  setStateFromProps (props, context) {
    const isEdit = !!context.router.getCurrentQuery().isEdit
    const canEdit = !props.account.get('isAdmin') === props.property.get('isPrivate')

    context.store.metadata = context.store.metadata
      .setIn(['property', 'county', 'enumValues'], props.metro.get('countiesM'))
      .setIn(['property', 'state', 'enumValues'], props.metro.get('statesM'))

    this.state = {
      property: props.property,
      undoProperty: props.property,
      photos: props.photos.get('data'),
      improvements: props.improvements.get('data'),
      landUses: props.landUses.get('data'),
      editing: isEdit && canEdit,
      onCertify: ::this.certifyField,
      link: createLinkToImmutable(this, 'property', {
        unit1BrCt: ::this.calcUnitCt,
        unit2BrCt: ::this.calcUnitCt,
        unit3BrCt: ::this.calcUnitCt,
        unitEffCt: ::this.calcUnitCt,
        landSf: ::this.calcLandSfChange,
        gbaSf: ::this.calcLndBldgPct,
        acresCt: ::this.calcLandSf
      })
    }
  }

  calcLandSfChange (property, keyPath, value) {
    this.calcLndBldgPct(property, keyPath, value)
    this.calcAcresCt(property, keyPath, value)
  }

  calcUnitCt (property, keyPath, value) {
    let unit1BrCtVal = ensureNumber(property.get('unit1BrCt'))
    let unit2BrCtVal = ensureNumber(property.get('unit2BrCt'))
    let unit3BrCtVal = ensureNumber(property.get('unit3BrCt'))
    let unitEffCtVal = ensureNumber(property.get('unitEffCt'))

    switch (R.last(keyPath)) {
      case 'unit1BrCt':
        unit1BrCtVal = ensureNumber(value)
        break
      case 'unit2BrCt':
        unit2BrCtVal = ensureNumber(value)
        break
      case 'unit3BrCt':
        unit3BrCtVal = ensureNumber(value)
        break
      case 'unitEffCt':
        unitEffCtVal = ensureNumber(value)
        break
    }

    property.set('unitCt', (unit1BrCtVal + unit2BrCtVal + unit3BrCtVal + unitEffCtVal).toFixed(0))
  }

  calcLndBldgPct (property, keyPath, value) {
    let landSfVal = ensureNumber(property.get('landSf'))
    let gbaSfVal = ensureNumber(property.get('gbaSf'))

    switch (R.last(keyPath)) {
      case 'landSf':
        landSfVal = ensureNumber(value)
        break
      case 'gbaSf':
        gbaSfVal = ensureNumber(value)
        break
    }

    if (landSfVal && gbaSfVal) {
      property.set('lndBldgPct', (landSfVal / gbaSfVal).toFixed(2))
    }
  }

  calcAcresCt (property, keyPath, value) {
    property.set('acresCt', (ensureNumber(value) / 43560).toFixed(2))
  }

  calcLandSf (property, keyPath, value) {
    property.set('landSf', (ensureNumber(value) * 43560).toFixed(0))
  }

  async handleEditPublic () {
    const { store, router } = this.context
    const { property } = this.props
    const id = property.get('id')

    try {
      const prvProp = await store.post(`/records/${id}/sync`)
      router.transitionTo('record', {
        id: prvProp.get('id')
      }, { isEdit: true })
    } catch (er) {
      console.error(er)
    }
  }

  handleEditToggle () {
    const { account } = this.props
    const { notify } = this.context
    const { property, editing, undoProperty } = this.state
    const isPublicEditForNonAdmin = !account.get('isAdmin') && !property.get('isPrivate')

    if (isPublicEditForNonAdmin) return this.handleEditPublic()

    if (editing && !property.equals(undoProperty)) {
      return notify('Please save or reject changes')
    }
    this.setState({
      editing: !editing,
      undoProperty: property
    })
  }

  handleLandUseChange (landUses) {
    this.setState({ landUses })
  }

  handleImprovementChange (improvements) {
    this.setState({ improvements })
  }

  handlePhotoChange (photos) {
    this.setState({ photos })
  }

  handleDuplicateChange (property) {
    this.setState({ editing: false, showDuplicateModal: false, property })
    global.scrollTo(0, 0)
  }

  handleShareToggle () {
    const { property } = this.state
    this.setState({ property: property.set('unsharable', !property.get('unsharable')) })
  }

  undoChanges () {
    this.setState({
      editing: false,
      property: this.state.undoProperty
    })
  }

  async share () {
    const { property } = this.state
    const { store, notify } = this.context
    const id = property.get('id')

    try {
      const sharedProperty = await store.post(`/properties/${id}/share?expanded=true`)
      this.setState({ property: sharedProperty })
      notify('Property data shared')
    } catch (er) {
      console.error(er)
      notify(er.response.body.error)
    }
  }

  async unlock () {
    const { property } = this.state
    const { store, notify } = this.context
    const id = property.get('id')

    try {
      const unlockedProperty = await store.post(`/properties/${id}/unlock?expanded=true`)
      const landUses = await store.get(`/properties/${id}/landUse`)
      const improvements = await store.get(`/properties/${id}/improvements`)
      this.setState({
        property: unlockedProperty,
        landUses: landUses.get('data'),
        improvements: improvements.get('data')
      })
      notify('Property data unlocked')
    } catch (er) {
      console.error(er)
      notify(er.response.body.error)
    }
  }

  async certifyField (field) {
    const { account } = this.props
    const { property } = this.state
    const { store, notify } = this.context
    const id = property.get('id')

    try {
      await store.post(`/properties/${id}/certifyField`, { field })
      this.setState({
        property: property.setIn(['fieldData', field, 'certById'], account.get('id'))
      })
      notify('Field certified successfully')
    } catch (er) {
      console.error(er)
      notify(er.response.body.error)
    }
  }

  async updateProperty (evt) {
    const { property } = this.state
    const { store, notify } = this.context
    const id = property.get('id')

    try {
      const updatedProperty = await store.post(`/properties/${id}?expanded=true`, property)
      this.setState({ editing: false, property: property.mergeDeep(updatedProperty) })
      notify('Changes saved successfully')
    } catch (er) {
      console.error(er)
      this.setState({
        errors: er.response.body.details
      })
    }
    global.scrollTo(0, 0)
  }

  openDuplicateModal () {
    this.setState({ showDuplicateModal: true })
  }

  cancelDuplicateModal () {
    this.setState({ showDuplicateModal: false })
  }

  openDestroyModal () {
    this.setState({ showDestroyModal: true })
  }

  cancelDestroyModal () {
    this.setState({ showDestroyModal: false })
  }

  async destroyRecord (evt) {
    const { property } = this.state
    const { store, router, notify } = this.context
    const id = property.get('id')

    try {
      await store.delete(`/records/${id}`)
      router.transitionTo('my-records')
    }
    catch (er) {
      console.error(er)
      notify(er.response.body.error)
    }
  }

  renderUserTd (field) {
    const { property, link } = this.state
    const userMap = property.get('userMap')
    const isPrivate = property.get('isPrivate')
    const user = property.getIn(['fieldData', field, 'userId'])
    const value = link(field).value

    return (
      <td width={37}>
        {user && value &&
          <a href={`mailto:${userMap.getIn([user, 'email'])}`}>
            <img
              width={20}
              className="img-circle"
              title={(isPrivate ? 'Last edited by ' : 'Added by ') + userMap.getIn([user, 'username'])}
              src={`/api/v1/users/${user}.jpg?size=40`} />
          </a>
        }
      </td>
    )
  }

  renderOtherFeatureTable () {
    const { property, editing, link } = this.state
    const isLocked = property.has('pointsWorth')
    const placeholders = Map({
      otherFeat1: 'i.e.; Porch -or- Deck',
      otherFeatD1: 'i.e.; 12x12 cedar',
      otherFeat2: 'i.e.; Sauna',
      otherFeatD2: 'i.e.; 8x8 wood fired'
    })

    const fields = [1, 2, 3].map(idx => {
      const feat = `otherFeat${idx}`
      const desc = `otherFeatD${idx}`

      return (
        <tr key={idx}>
          <td>
            {editing
              ? <Input type="text" valueLink={link(feat)} name={feat} placeholder={placeholders.get(feat)} />
              : isLocked ? <i className="text-muted">Data available...</i> : link(feat).value || <span>&nbsp;</span>
            }
          </td>
          {this.renderUserTd(feat)}
          <td>
            {editing
              ? <Input type="text" valueLink={link(desc)} name={desc} placeholder={placeholders.get(desc)}  />
              : isLocked ? <i className="text-muted">Data available...</i> : link(desc).value || <span>&nbsp;</span>
            }
          </td>
          {this.renderUserTd(desc)}
        </tr>
      )
    })

    return (
      <div>
        <h5><b>Building: Other features</b></h5>
        <Table bordered className="redi-component-editable-table">
          <thead>
            <tr>
              <th colSpan={2}>Feature</th>
              <th colSpan={2}>Description</th>
            </tr>
          </thead>
          <tbody>
            {fields}
          </tbody>
        </Table>
      </div>
    )
  }

  render () {
    const { account } = this.props
    const metadata = this.context.store.metadata.get('property')
    const {
      property,
      photos,
      improvements,
      landUses,
      errors,
      link,
      editing,
      undoProperty,
      showDestroyModal,
      showDuplicateModal,
      onCertify
    } = this.state

    const isAg = property.get('genUseTM').contains('Agriculture')
    const isSfr = property.get('genUseTM').contains('Single Family Residential')
    const isPrivate = property.get('isPrivate')
    const isAdmin = account.get('isAdmin')
    const canEdit = (isAdmin && !isPrivate) || isPrivate

    return (
      <DocumentTitle title={property.get('address')}>
        <div>
          <RecordNav property={property} account={account} onShareToggle={::this.handleShareToggle}>
            <PropertyActions
              account={account}
              property={property}
              editing={editing}
              handleEdit={::this.handleEditToggle} />
          </RecordNav>

          {isPrivate && property.has('pointsAvailable') && !property.get('unsharable') &&
            <Alert>
              <Button
                onClick={::this.share}
                bsStyle="info"
                bsSize="xsmall"
                className="pull-right">
                Share
              </Button>
              Earn <b>{property.get('pointsAvailable')} points</b>&nbsp;
              (or more when your data is certified by others) by sharing this property publicly.
            </Alert>
          }

          {!isPrivate && property.has('pointsWorth') &&
            <Alert bsStyle="success">
              <Button
                onClick={::this.unlock}
                bsStyle="success"
                bsSize="xsmall"
                className="pull-right">
                Unlock
              </Button>
              For <b>{property.get('pointsWorth')} points</b> you can unlock this property,&nbsp;
              including photos, and enjoy unlimited future updates for free.
            </Alert>
          }

          {!isPrivate && property.has('duplicate') &&
            <Alert bsStyle="warning">
              <ButtonLink
                to="record"
                params={{ id: property.getIn(['duplicate', 'id']) }}
                bsStyle="warning"
                bsSize="xsmall"
                className="pull-right">
                Go to master record
              </ButtonLink>
              This property has been marked as a duplicate.&nbsp;
              Although still fully functional, we recommended using the master property record.
            </Alert>
          }

          <Row>
            <Col xs={12}>
              <PhotoGallery
                editable={editing && canEdit}
                photos={photos}
                onChange={::this.handlePhotoChange}
                property={undoProperty}
              />
            </Col>

            <Col xs={6}>
              <EditableTable
                title="Site: Address Details"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                adminEditOnly={List(['county', 'state'])}
                fields={List([
                  'strNoPre',
                  'strNo',
                  'strDrPre',
                  'strName',
                  'strSuf',
                  'strDrSuf',
                  'strUnit',
                  'county',
                  'city',
                  'state',
                  'zip',
                  'intersect'
                ])}
              />

              <EditableTable
                title="Site: Neighborhood"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'nhT',
                  'nhRatiosT',
                  'nhBoundries',
                  'nhDesc',
                  'location'
                ])}
              />

              <EditableTable
                title="Site: Attributes"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                adminEditOnly={List(['pid'])}
                fields={List([
                  'catylistId',
                  'pid',
                  'addnPidsM',
                  'genUseTM',
                  'landSf',
                  'acresCt',
                  'dimensions',
                  'shapeT',
                  'frontageStr',
                  'frontageFt',
                  'strSurfT',
                  'hasCurbing',
                  'sewerT',
                  'waterT',
                  'hasStmSewer'
                ])}
              />

              <EditableTable
                title="Site: Attributes (Other)"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'hasElec',
                  'elecOther',
                  'hasGas',
                  'gasOther',
                  'hasWater',
                  'waterOther',
                  'hasSewer',
                  'sewerOther',
                  'telephone',
                  'strT',
                  'allyT'
                ])}
              />

              <EditableTable
                title="Site: Easements"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'nonStdEas'
                ])}
              />

              <EditableTable
                title="Site: Legal"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'legalDesc'
                ])}
              />

              <EditableTable
                title="Site: Comments"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'siteRemarks'
                ])}
              />

              <EditableTable
                title="Building: Area"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'gbaSf',
                  'totalNraSf',
                  'retailSf',
                  'gbaFlrSf',
                  'firstFlrSf',
                  'upperFlrSf',
                  'gbaDesc',
                  'mezzSf',
                  'mezzUse',
                  'bsmtUse',
                  'garageCt',
                  !isSfr && 'carGarSpCt',
                  'garageT',
                  'otherBldgs',
                  'otherSf',
                  'otherSfDesc'
                ])}
              />

              <EditableTable
                title="Building: Office breakdown"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'bldgClsT',
                  'officeSf',
                  'officeFlrT',
                  'officeHtT',
                  'officeAirT',
                  'officeQlT'
                ])}
              />

              <EditableTable
                title="Building: Tenancy"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'tenantT',
                  'tenantCt',
                  'occupPct',
                  'estActT',
                  'vacancyPct',
                  'ownedT'
                ])}
              />

              <EditableTable
                title="Building: Industrial"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'clrHtHighFt',
                  'clrHtLowFt',
                  'clrHtAvgFt',
                  'railSrv',
                  'techSf',
                  'whseSf'
                ])}
              />

              <EditableTable
                title="Building: General"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'hasUnitsOne',
                  'hasUnitsAcc',
                  'progT',
                  'design'
                ])}
              />

              <EditableTable
                title="Building: Attic"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'atticTM'
                ])}
              />

              <EditableTable
                title="Building: Foundation"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'bsmtT',
                  'bsmtSf',
                  'bsmtCondTM',
                  'bsmtFinPct',
                  'rmBlwGrade'
                ])}
              />

              <EditableTable
                title="Building: HVAC"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'heatSfrT',
                  'heatOther',
                  'heatingFuel',
                  'coolT',
                  'coolOther'
                ])}
              />

              <EditableTable
                title="Building: Exterior"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'extFdtn',
                  'extWalls',
                  'extRoof',
                  'extGutter',
                  'extWindow',
                  'extStrmSI',
                  'extScreens'
                ])}
              />

              <EditableTable
                title="Building: Comments"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'bldgRemarks'
                ])}
              />
            </Col>

            <Col xs={6}>
              {property.has('latitude') &&
                <div>
                  <h5><b>Site: Interactive map</b></h5>
                  <GeocodeMap
                    latitude={property.get('latitude')}
                    longitude={property.get('longitude')} />
                </div>
              }
              <EditableTable
                title="Site: Lay of the land"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'zoning',
                  'floodZoneTM',
                  'topoT',
                  'soilT',
                  'soilsCondT',
                  'soilsDesc',
                  'isContam',
                  'contamDesc',
                  'view',
                  'zoneLegalT',
                  'hasWtrFrntg',
                  'wtrFrntName',
                  'wtrFrntFt'
                ])}
              />

              <EditableTable
                title="Site: Parking"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'parkingT',
                  'parkingCt',
                  'parkingOwnT',
                  'lndBldgPct'
                ])}
              />

              <EditableTable
                title="Site: MH pads"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'mobl1PdCt',
                  'mobl2PdCt',
                  'mobl3PdCt',
                  'moblOthCt'
                ])}
              />

              <EditableTable
                title="Building: Traits"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'builtYr',
                  'actAge',
                  'effAge',
                  'storiesCt',
                  'bldgCt'
                ])}
              />

              <EditableTable
                title="Building: Textiles"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'frameTM',
                  'floorT',
                  'flrCoverT',
                  'exteriorTM',
                  'roofT'
                ])}
              />

              <EditableTable
                title="Building: Loading bays"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'dockCt',
                  'drvDoorsCt',
                  'dockHtFt'
                ])}
              />

              <EditableTable
                title="Building: Features"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'decks',
                  'sprinklPct',
                  'elevCargoCt',
                  'elevPaxCt',
                  'escalatrCt',
                  'amenities',
                  'features',
                  'bldgEff',
                  'airT',
                  'heatT',
                  'heatFuelT',
                  'condT',
                  'qualityT',
                  'appliances',
                  'hasLaundry',
                  'hasPool'
                ])}
              />

              <EditableTable
                title="Building: Power"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'elecSrv',
                  'elecPhaseT',
                  'elecA',
                  'elecV'
                ])}
              />

              <EditableTable
                title="Building: MUH unit mix"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'unitCt',
                  'unitEffCt',
                  'avgEffSf',
                  'unit1BrCt',
                  'avg1BrSf',
                  'unit2BrCt',
                  'avg2BrSf',
                  'unit3BrCt',
                  'avg3BrSf'
                ])}
              />

              <EditableTable
                title="Building: Amenities"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'amenFeatTM',
                  'ppdDesc',
                  'energyEff'
                ])}
              />

              <EditableTable
                title="Building: Interior"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'intFloors',
                  'intWalls',
                  'intTrimFin',
                  'intBaFloor',
                  'intBaWnsct'
                ])}
              />

              <EditableTable
                title="Building: Vehicle"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'garagePkTM',
                  'garageSfrTM',
                  'carDrvPaCt',
                  isSfr && 'carGarSpCt',
                  'carPrtStCt',
                  'carDrvSurf'
                ])}
              />

              <EditableTable
                title="Building: Appliances"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'applianceTM'
                ])}
              />

              <EditableTable
                title="Building: Above grade"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'roomCt',
                  'bedroomCt',
                  'bathroomCt'
                ])}
              />

              <EditableTable
                title="Building: Other"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'funcUtil'
                ])}
              />

              {isSfr && this.renderOtherFeatureTable()}

              <EditableTable
                title="Land: Improvements"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'lndImp1',
                  'lndImp2',
                  'lndImp3',
                  'lndImp4',
                  'lndImp5',
                  'lndImp6',
                  'rightsTM'
                ])}
              />

              <EditableTable
                title="Land: Climatic"
                link={link}
                errors={errors}
                editable={editing}
                metadata={metadata}
                data={property}
                account={account}
                onCertify={onCertify}
                fields={List([
                  'annualPrcCt',
                  'ffdCt',
                  'elevAvgCt'
                ])}
              />
            </Col>
          </Row>

          <EditableTable
            link={link}
            errors={errors}
            editable={editing}
            metadata={metadata}
            data={property}
            account={account}
            onCertify={onCertify}
            fields={List([
              'linksM',
              'rediRemarks'
            ])}
          />

          {isPrivate &&
            <PersonalMapping
              account={account}
              type="property"
              link={link}
              editable={editing}
              data={property.get('customFlds')}
              mapping={property.get('personalMapping')} />
          }

          {isAg &&
            <LandUseTable
              account={account}
              landUses={landUses}
              metadata={metadata.get('landUse')}
              property={undoProperty}
              onChange={::this.handleLandUseChange}
              editable={editing} />
          }

          {isAg &&
            <ImprovementsTable
              account={account}
              improvements={improvements}
              metadata={metadata.get('improvements')}
              property={undoProperty}
              onChange={::this.handleImprovementChange}
              editable={editing} />
          }

          {editing &&
            <div>
              <div className="pull-right">
                {isAdmin && canEdit && !property.has('duplicate') &&
                  <Button
                    onClick={::this.openDuplicateModal}
                    bsStyle="warning">
                    Mark as duplicate
                  </Button>
                }
                &nbsp;
                <Button
                  onClick={::this.openDestroyModal}
                  bsStyle="danger">
                  Remove record
                </Button>
              </div>
              <Button
                className="hotkeys-save"
                onClick={::this.updateProperty}
                bsStyle="primary">
                Save changes
              </Button>
              &nbsp;
              <Button
                className="hotkeys-undo"
                onClick={::this.undoChanges}
                bsStyle="warning">
                Reject changes
              </Button>
            </div>
          }

          {showDestroyModal &&
            <ConfirmModal
              actionText="Remove this record"
              onAction={::this.destroyRecord}
              onCancel={::this.cancelDestroyModal}
              bsStyle="danger"
              message={
                <p>
                  This will remove this record and <b className="text-danger">ALL</b> associated transactions.&nbsp;
                  If you wish to keep transactions, remove their association with this record first.
                </p>
              }
            />
          }
          <MarkDuplicateModal
            property={property}
            onChange={::this.handleDuplicateChange}
            onHide={::this.cancelDuplicateModal}
            show={showDuplicateModal}
          />
        </div>
      </DocumentTitle>
    )
  }
}

export default PropertyDetail



// WEBPACK FOOTER //
// ./modules/shared/pages/record/propertyDetail.js